import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { fetchUserId, fetchCompanyId } from "../../helpers/CompanyId";
import api from "../../api";
import Navbar from "../../pages/Navbar";
import { months } from "../../helpers/constants";

const VPayslip = () => {
  const [companyId, setCompanyId] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [empId, setEmpId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  // Fetch companyId and empId on component mount
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const userId = await fetchUserId();
        if (!userId) {
          setError("User ID not found");
          setLoading(false);
          return;
        }

        const fetchedCompanyId = await fetchCompanyId(userId);
        if (!fetchedCompanyId) {
          setError("Company ID not found");
          setLoading(false);
          return;
        }

        // Set the companyId
        setCompanyId(fetchedCompanyId);

        // Fetch employee details to get empId
        const response = await api.get(
          `/api/employee/retrieve_employee/${userId}/`
        );
        if (response.data && response.data.work_details) {
          setEmpId(response.data.work_details.empId);
        } else {
          setError("Employee details not found");
        }
      } catch (err) {
        setError("Error fetching company or employee details");
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchDetails();
  }, []);

  // Handle API call to generate the payslip
  const generatePayslip = async () => {
    if (!empId || !month || !year || !companyId) {
      setError("Please select Employee ID, Month, Year, and Company ID");
      return;
    }

    setLoading(true);
    setError("");
    setPdfUrl("");

    try {
      const apiUrl = `/api/payslip/${empId}/${month}-${year}/${companyId}/`;
      const response = await api.get(apiUrl, { responseType: "blob" });

      // Create a blob URL to display the PDF
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfBlobUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfBlobUrl);
    } catch (err) {
      console.error(err);
      setError("Failed to generate payslip");
    }

    setLoading(false);
  };

  // Handle Download
  const downloadPdf = () => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.setAttribute("download", `payslip_${empId}_${year}_${month}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <Navbar />

      <Box sx={{ margin: "50px auto", maxWidth: 600 }}>
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" alignItems="center" sx={{ marginBottom: 2 }}>
          <TextField
            select
            label="Month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            sx={{ marginRight: 2, width: "200px" }}
            fullWidth
          >
            {months.map((monthName, index) => (
              <MenuItem key={index + 1} value={index + 1}>
                {monthName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            sx={{ marginRight: 2, width: "200px" }}
            fullWidth
          >
            {Array.from(
              { length: 5 },
              (_, i) => new Date().getFullYear() - i
            ).map((yr) => (
              <MenuItem key={yr} value={yr}>
                {yr}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            color="primary"
            onClick={generatePayslip}
            disabled={loading}
            sx={{ width: "250px", height: "54px" }}
          >
            {loading ? <CircularProgress size={24} /> : "Generate Payslip"}
          </Button>
        </Box>
      </Box>

      {/* Payslip Preview Box: Displayed below the buttons after generating payslips */}
      {pdfUrl && (
        <Box
          sx={{
            maxWidth: 800,
            margin: "50px auto",
            padding: 4,
            boxShadow: 3,
            borderRadius: 2,
            backgroundColor: "#fff",
          }}
        >
          <iframe
            src={pdfUrl}
            title="Payslip Preview"
            style={{ width: "100%", height: "500px", border: "1px solid #ccc" }}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={downloadPdf}
            sx={{ marginTop: 2 }}
          >
            Download PDF
          </Button>
        </Box>
      )}
    </div>
  );
};

export default VPayslip;
